import { useMemo } from "react";
import { usePageVisibility } from "hooks/usePageVisibility";

/**
 * Hook that makes a refetchInterval conditional on page visibility.
 *
 * When the page/tab is not visible, refetching stops automatically.
 * When the page becomes visible again, refetching resumes.
 *
 * This is useful for any query that polls for updates but should
 * stop when the user is not actively viewing the page.
 *
 * @param refetchInterval - The refetch interval from React Query options
 *   Can be a number (milliseconds), false, or a function that returns number | false
 * @returns A conditional refetch interval that respects page visibility
 *
 * @example
 * ```ts
 * const conditionalInterval = useConditionalRefetchInterval(30_000);
 *
 * useQuery({
 *   queryKey: ['data'],
 *   queryFn: fetchData,
 *   refetchInterval: conditionalInterval,
 *   refetchIntervalInBackground: false,
 * });
 * ```
 */
export const useConditionalRefetchInterval = <TData = any>(
  refetchInterval?: number | false | ((data: TData | undefined, query: any) => number | false)
): number | false | ((data: TData | undefined, query: any) => number | false) => {
  const isPageVisible = usePageVisibility();

  return useMemo(() => {
    if (!refetchInterval) {
      return false;
    }

    // If refetchInterval is a function, wrap it to check visibility
    if (typeof refetchInterval === "function") {
      const refetchIntervalFn = refetchInterval;
      return (data: TData | undefined, query: any) => {
        if (!isPageVisible) {
          return false;
        }
        return refetchIntervalFn(data, query);
      };
    }

    // If refetchInterval is a number, return it only when visible
    return isPageVisible ? refetchInterval : false;
  }, [refetchInterval, isPageVisible]);
};
